// cSpell:ignore Aandeel afrekening berekening categorie deze facturen Huurcontract Klik kost lopend maanden pand Privaat voor
// An "i" whose tooltip lists what applies as a label and its value (Lewis,
// 2026-10-17: the tooltips were blocks of text):
//
//   Huurcontract         01/07/2026 – lopend
//   In deze afrekening   01/07/2026 t/m 31/12/2026 · 6 maanden
//
// The labels in a muted column, the values in the text's own color, at
// most about 420px wide; an optional note in the muted color under them
// ("Klik op een categorie voor de facturen en de berekening."). Its
// accessible name is every row in one sentence.
//
// A plain list (`lines`), one item per line: the units of an owner of
// several, each by its full address and weight (Lewis, 2026-10-19).
//
// A row can carry a compact list under it (`details`), across the whole
// tooltip: each unit of a key with its percentage under the "Aandeel" of a
// summary row's amount (Lewis, 2026-10-19).
//
// As a column header's help (`help`, Lewis, 2026-10-18), a "?" whose rows
// are terms and what they mean, each a short paragraph that wraps under its
// term ("Privaat: een kost van één pand …"), with space between them.
//
// Or any content of its own (`content`, a table of the VAT per rate), its
// accessible name in `label`.

import { Icon, Tooltip } from "@rentiohq/web-shared/dist/components";
import { FontWeights } from "@rentiohq/web-theme";
import React from "react";
import styled from "styled-components";
import { IInfoRow } from "../../../../provisionMonthsHint.utils";

const MAX_WIDTH = 420;

// A row, with an optional compact list under it.
export interface IInfoTipRow extends IInfoRow {
  details?: IInfoRow[];
}

const InfoIcon = styled.span`
  display: inline-flex;
  margin-left: 4px;
  vertical-align: middle;
  cursor: help;
  color: ${({ theme }) => theme.getColor("gray", 600)};
`;

const Rows = styled.dl`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  column-gap: 16px;
  row-gap: 4px;
  margin: 0;
  text-align: left;

  dt {
    color: ${({ theme }) => theme.colors.neutral40};
    font-weight: ${FontWeights.Regular};
  }
  dd {
    margin: 0;
  }
  .details {
    grid-column: 1 / -1;
    margin-bottom: 4px;
  }
  .details > div {
    display: flex;
    justify-content: space-between;
    gap: 16px;
  }
  .details > div > span:first-child {
    color: ${({ theme }) => theme.colors.neutral40};
  }
`;

const Terms = styled.div`
  text-align: left;

  p {
    margin: 0;
  }
  p + p {
    margin-top: 10px;
  }
  strong {
    font-weight: ${FontWeights.Medium};
  }
`;

const Note = styled.div`
  margin-top: 6px;
  text-align: left;
  color: ${({ theme }) => theme.colors.neutral40};
`;

const Lines = styled.div`
  text-align: left;
  div + div {
    margin-top: 4px;
  }
`;

export const InfoTip: React.FC<{
  rows: IInfoTipRow[];
  lines?: string[];
  note?: string;
  // A "?" whose rows explain terms (a column header's help).
  help?: boolean;
  content?: React.ReactNode;
  label?: string;
}> = ({ rows, lines = [], note, help, content, label }) => {
  if (rows.length === 0 && lines.length === 0 && !note && !content) {
    return null;
  }
  return (
    <Tooltip
      tooltipContentStyle={{ maxWidth: MAX_WIDTH }}
      tooltipContent={
        <>
          {content}
          {rows.length > 0 && help && (
            <Terms>
              {rows.map(row => (
                <p key={row.label}>
                  <strong>{row.label}:</strong> {row.value}
                </p>
              ))}
            </Terms>
          )}
          {rows.length > 0 && !help && (
            <Rows>
              {rows.map(row => (
                <React.Fragment key={`${row.label}:${row.value}`}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                  {row.details && row.details.length > 0 && (
                    <div className="details">
                      {row.details.map(detail => (
                        <div key={`${detail.label}:${detail.value}`}>
                          <span>{detail.label}</span>
                          <span>{detail.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </Rows>
          )}
          {lines.length > 0 && (
            <Lines>
              {/* A line can repeat: a unit's weight by two keys. */}
              {lines.map((line, index) => (
                <div key={`${index}:${line}`}>{line}</div>
              ))}
            </Lines>
          )}
          {note && <Note>{note}</Note>}
        </>
      }
    >
      <InfoIcon
        role="img"
        aria-label={[
          ...(label ? [label] : []),
          ...rows.flatMap(row => [
            `${row.label}: ${row.value}`,
            ...(row.details ?? []).map(
              detail => `${detail.label}: ${detail.value}`,
            ),
          ]),
          ...lines,
          ...(note ? [note] : []),
        ].join(". ")}
        tabIndex={0}
      >
        <Icon
          source={help ? "questionMark" : "informationCircle"}
          size="small"
        />
      </InfoIcon>
    </Tooltip>
  );
};
